// Client layer (TS-2.5.03) - UC-1's detail/edit screen. Composes the service hook
// (useProjectDetail), the reference vocabulary each sub-section needs, and ProjectForm/
// MilestonesSection/NotesSection; owns the save flow (create vs. update, the D-198 entityKey
// navigation on create, and turning a SaveResult's staleness/issues/warnings into non-blocking
// WarningsBanner items - never a modal, per this batch's staleness guidance) and the dirty-
// navigation guard. `/projects/new` and `/projects/:projectKey` share this one component
// (decomposition.md's own preference for one shape over a create/edit fork elsewhere in this
// codebase - PortfolioDataManager's SaveProjectAsync is itself one method for both).
import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../api/ApiError";
import { PortfolioRoles } from "../../api/generated/contracts";
import type { ValidationIssue } from "../../api/generated/contracts";
import { portfolioDataApi } from "../../api/portfolioData";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { WarningsBanner } from "../../components/WarningsBanner";
import type { WarningsBannerItem } from "../../components/WarningsBanner";
import { useCallerPermissions } from "../../hooks/useCallerPermissions";
import { useDirtyNavigationGuard } from "../../hooks/useDirtyNavigationGuard";
import { useProducts } from "../../hooks/useProducts";
import { useReferenceList } from "../../hooks/useReferenceList";
import { strings } from "../../strings";
import { MilestonesSection } from "./MilestonesSection";
import { NotesSection } from "./NotesSection";
import { ProjectForm } from "./ProjectForm";
import { useOwnerSuggestions } from "./hooks/useOwnerSuggestions";
import { useProjectDetail } from "./hooks/useProjectDetail";
import { draftFromRecord, EMPTY_PROJECT_DRAFT, rebaseDraftAfterSave, recordFromDraft } from "./lib/projectDraft";
import type { ProjectDraft } from "./lib/projectDraft";
import { buildProductOptions } from "./lib/productOptions";
import { duplicateBusinessIdBannerItems, issueBannerItems, stalenessBannerItems } from "./lib/saveBanners";
import "./projects.css";

const EDIT_ROLES = [PortfolioRoles.Contributor, PortfolioRoles.ProjectManager, PortfolioRoles.Admin];
const S = strings.projects.detail;

export function ProjectDetailPage() {
  const { projectKey: routeParam } = useParams<{ projectKey: string }>();
  const isCreate = routeParam === "new" || routeParam === undefined;
  const projectKey = isCreate ? null : (routeParam ?? null);
  const navigate = useNavigate();
  const { hasRole } = useCallerPermissions();
  const canEdit = hasRole(...EDIT_ROLES) && (isCreate || projectKey !== null);

  const { detail, loading, error, refresh, setDetail } = useProjectDetail(projectKey);
  const { list: teamList } = useReferenceList("team");
  const { list: statusList } = useReferenceList("project.status");
  const { list: completionList } = useReferenceList("milestone.completion");
  const { list: noteTypeList } = useReferenceList("note.type");
  const { list: noteStatusList } = useReferenceList("note.status");
  const { list: placeholderList } = useReferenceList("date.placeholder");
  const { owners: ownerOptions } = useOwnerSuggestions();
  // includeRetired: a project already carrying a retired product must still show it labeled
  // "(retired)" rather than have it vanish from the picker (D-268) - see lib/productOptions.ts.
  const { products } = useProducts(true);

  const placeholderOptions = useMemo(
    () => (placeholderList?.values ?? []).map((value) => ({ token: value.code, label: value.canonicalLabel })),
    [placeholderList],
  );

  const [draft, setDraft] = useState<ProjectDraft>(EMPTY_PROJECT_DRAFT);
  const [issues, setIssues] = useState<readonly ValidationIssue[]>([]);
  const [staleness, setStaleness] = useState<readonly WarningsBannerItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [deleting, setDeleting] = useState(false);

  const productOptions = useMemo(() => buildProductOptions(products ?? [], draft.productKey), [products, draft.productKey]);

  const dirtyGuard = useDirtyNavigationGuard();

  // Hydrates the draft from the server ONCE per project key, not every time `detail` changes.
  // `detail` also changes on a background invalidation refresh (useProjectDetail's own
  // useInvalidation subscription re-GETs the row on ANY change broadcast for this entity type,
  // including the echo of a save this same tab just made) - if this effect re-ran on every such
  // change it would silently overwrite the draft, discarding an edit made since, and racing with
  // the same rebase handleSave performs on save success below. Keying the "already hydrated"
  // check off the entity's own key (rather than depending on `detail`'s object identity) is what
  // makes an actual navigation to a different project (or into create mode) still hydrate.
  const hydratedKeyRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (isCreate) {
      if (hydratedKeyRef.current !== "new") {
        setDraft(EMPTY_PROJECT_DRAFT);
        hydratedKeyRef.current = "new";
      }
      return;
    }

    if (detail && hydratedKeyRef.current !== detail.project.key) {
      setDraft(draftFromRecord(detail.project));
      hydratedKeyRef.current = detail.project.key;
    }
  }, [isCreate, detail]);

  function updateDraft(patch: Partial<ProjectDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
    dirtyGuard.setDirty(true);
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);

    try {
      const request = { project: recordFromDraft(draft), concurrencyToken: draft.concurrencyToken };
      const result = isCreate
        ? await portfolioDataApi.createProject(request)
        : await portfolioDataApi.updateProject(projectKey as string, request);

      setIssues(result.issues);
      setStaleness(stalenessBannerItems(result.staleness));

      if (result.saved && result.entity) {
        const savedProject = result.entity.project;
        setDetail(result.entity);
        // A functional update (not `draftFromRecord(savedProject)`) so this rebases onto whatever
        // the draft holds AT THIS MOMENT - including an edit typed while the request was in
        // flight - rather than discarding it. The key/concurrencyToken it adopts are always the
        // ones this response just returned, which is what keeps a very next save's request from
        // carrying the pre-save (now stale) token - see rebaseDraftAfterSave's own header.
        setDraft((current) => rebaseDraftAfterSave(current, savedProject, result.concurrencyToken));
        hydratedKeyRef.current = savedProject.key;
        dirtyGuard.setDirty(false);

        const newKey = savedProject.key;
        if (isCreate && newKey) {
          navigate(`/projects/${newKey}`, { replace: true });
        }
      }
    } catch (cause) {
      setSaveError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The project could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!projectKey) return;
    setDeleting(true);
    try {
      await portfolioDataApi.deleteProject(projectKey);
      navigate("/projects", { replace: true });
    } finally {
      setDeleting(false);
    }
  }

  function goToList() {
    dirtyGuard.guardedNavigate(() => navigate("/projects"));
  }

  if (!isCreate && loading && !detail) {
    return (
      <section className="eg-project-detail">
        <h1>{S.loading}</h1>
      </section>
    );
  }

  if (!isCreate && error) {
    return (
      <section className="eg-project-detail">
        <div role="alert">
          <p>{S.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      </section>
    );
  }

  const banners = [...staleness, ...issueBannerItems(issues), ...duplicateBusinessIdBannerItems(detail?.warnings ?? [])];

  return (
    <section className="eg-project-detail">
      <header className="eg-project-detail__header">
        <button type="button" onClick={goToList}>
          {S.backToList}
        </button>
        <h1>{isCreate ? S.newHeading : draft.name || detail?.project.name}</h1>
      </header>

      <WarningsBanner items={banners} />

      {saveError && (
        <div role="alert">
          <p>{saveError.message}</p>
        </div>
      )}

      <form className="eg-project-detail__card" onSubmit={(event) => void handleSave(event)}>
        <h2>{S.sectionHeading}</h2>
        <ProjectForm
          draft={draft}
          onChange={updateDraft}
          readOnly={!canEdit}
          issues={issues}
          teamOptions={teamList?.values ?? []}
          statusOptions={statusList?.values ?? []}
          ownerOptions={ownerOptions}
          placeholderOptions={placeholderOptions}
          productOptions={productOptions}
        />

        {canEdit && (
          <div className="eg-project-detail__form-actions">
            <button type="submit" className="eg-project-detail__btn--primary" disabled={saving}>
              {saving ? S.saving : S.save}
            </button>
            <button type="button" onClick={goToList}>
              {S.cancel}
            </button>
            {!isCreate && (
              <button
                type="button"
                className="eg-project-detail__btn--danger"
                disabled={deleting}
                onClick={() => void handleDelete()}
              >
                {S.delete}
              </button>
            )}
          </div>
        )}
      </form>

      {!isCreate && detail && (
        <>
          <MilestonesSection
            projectKey={detail.project.key ?? ""}
            milestones={detail.milestones}
            milestoneRecords={detail.milestoneRecords}
            completionOptions={completionList?.values ?? []}
            teamOptions={teamList?.values ?? []}
            placeholderOptions={placeholderOptions}
            readOnly={!canEdit}
            onChanged={refresh}
          />

          <NotesSection
            projectKey={detail.project.key ?? ""}
            notes={detail.notes}
            typeOptions={noteTypeList?.values ?? []}
            statusOptions={noteStatusList?.values ?? []}
            readOnly={!canEdit}
            onChanged={refresh}
          />
        </>
      )}

      <ConfirmDialog
        open={dirtyGuard.confirmPending}
        title={strings.projects.discardDialog.title}
        body={strings.projects.discardDialog.body}
        confirmLabel={strings.projects.discardDialog.confirm}
        cancelLabel={strings.projects.discardDialog.cancel}
        onConfirm={dirtyGuard.confirmDiscard}
        onCancel={dirtyGuard.cancelDiscard}
      />
    </section>
  );
}
